import type { PoseVitima } from './rigVitima'
import { fatorProtecaoColar } from './colarCervical'

/**
 * Dinâmica de Suspensão Veicular e Resposta Inercial da Vítima.
 *
 * Demonstra a física do veículo sobre suas molas antes da estabilização:
 * - A movimentação de socorristas e o vento induzem rolamento (roll) e arfagem (pitch).
 * - O corpo e a cabeça da vítima oscilam com a suspensão, gerando sobrecarga na coluna cervical.
 * - O calçamento sob as colunas (A/B/C) anula o efeito elástico das molas (k -> infinito).
 * - O colar cervical trava os movimentos da cabeça, amenizando o chicotinho cervical.
 */

export interface EstadoSuspensao {
  /** Deslocamento vertical do centro de massa (m) */
  deslocamentoY: number
  velocidadeY: number
  /** Inclinação lateral / roll (rad) */
  anguloRoll: number
  velocidadeRoll: number
  /** Inclinação longitudinal / pitch (rad) */
  anguloPitch: number
  velocidadePitch: number
  /** Deslocamento linear longitudinal / rolamento nas rodas (m) */
  deslocamentoZ: number
  velocidadeZ: number
  /** Se a suspensão elástica foi totalmente anulada por calços */
  suspensaoAnulada: boolean
  /** Se o rolamento nas rodas foi travado por cunhas */
  rolamentoTravado: boolean
}

export const ESTADO_SUSPENSAO_INICIAL: EstadoSuspensao = {
  deslocamentoY: 0,
  velocidadeY: 0,
  anguloRoll: 0,
  velocidadeRoll: 0,
  anguloPitch: 0,
  velocidadePitch: 0,
  deslocamentoZ: 0,
  velocidadeZ: 0,
  suspensaoAnulada: false,
  rolamentoTravado: false,
}

export interface ParametrosSuspensao {
  rigidezMolaY: number
  amortecimentoY: number
  rigidezRoll: number
  amortecimentoRoll: number
  rigidezPitch: number
  amortecimentoPitch: number
  rigidezZ: number
  amortecimentoZ: number
}

export const PARAMETROS_SUSPENSAO_PADRAO: ParametrosSuspensao = {
  rigidezMolaY: 48,
  amortecimentoY: 5.5,
  rigidezRoll: 62,
  amortecimentoRoll: 6.2,
  rigidezPitch: 55,
  amortecimentoPitch: 5.8,
  rigidezZ: 28,
  amortecimentoZ: 4.5,
}

export interface ForcaPerturbacao {
  forcaY?: number
  torqueRoll?: number
  torquePitch?: number
  forcaZ?: number
}

/** Um impulso da sequência de perturbação, disparado `atrasoMs` após o início. */
export interface PulsoPerturbacao {
  atrasoMs: number
  forca: ForcaPerturbacao
}

/**
 * Perturbação que passa pelas QUATRO suspensões.
 *
 * Um impulso único com rolagem e arfagem de mesmo sinal afunda um canto e
 * praticamente não mexe o oposto — o aluno via só um lado se mover e concluía
 * que os outros três já estavam estabilizados. A sequência comprime o conjunto,
 * depois balança para cada lado e para a frente e para trás, de modo que todo
 * canto sem apoio é carregado para baixo e para cima pelo menos uma vez.
 */
export const SEQUENCIA_PERTURBACAO_QUATRO_CANTOS: readonly PulsoPerturbacao[] = [
  { atrasoMs: 0, forca: { forcaY: -20, forcaZ: 4 } },
  { atrasoMs: 380, forca: { torqueRoll: 14 } },
  { atrasoMs: 760, forca: { torqueRoll: -14 } },
  { atrasoMs: 1140, forca: { torquePitch: 12 } },
  { atrasoMs: 1520, forca: { torquePitch: -12, forcaZ: -4 } },
]

export const DURACAO_PERTURBACAO_QUATRO_CANTOS_MS =
  SEQUENCIA_PERTURBACAO_QUATRO_CANTOS.at(-1)!.atrasoMs + 600

export interface RigidezDinamica {
  roll: number
  pitch: number
  vertical: number
  longitudinal: number
  apoiosLocais?: readonly PontoApoioSuspensao[]
}

export interface PontoApoioSuspensao {
  id?: string
  x: number
  z: number
  /** 0 = sem contato; 1 = vínculo rígido sem folga. */
  firmeza: number
}

export type PontoLocalVeiculo = Pick<PontoApoioSuspensao, 'x' | 'z'>

/**
 * Deslocamento vertical de um ponto da carroceria, pela aproximação de
 * pequenos ângulos do corpo rígido. Isso permite verificar o movimento em
 * cada coluna sem confundir um apoio local com o travamento do veículo todo.
 */
export function calcularDeslocamentoVerticalNoPonto(
  estado: Pick<EstadoSuspensao, 'deslocamentoY' | 'anguloRoll' | 'anguloPitch'>,
  ponto: PontoLocalVeiculo,
): number {
  return estado.deslocamentoY + estado.anguloRoll * ponto.x - estado.anguloPitch * ponto.z
}

type MovimentoVertical = [vertical: number, roll: number, pitch: number]

/**
 * Remove somente os movimentos incompatíveis com os pontos apoiados. As
 * direções ainda livres permanecem, por isso um único calço vira um pivô em
 * vez de congelar toda a carroceria.
 */
function aplicarVinculosLocais(
  movimento: MovimentoVertical,
  apoios: readonly PontoApoioSuspensao[],
): MovimentoVertical {
  const resultado: MovimentoVertical = [...movimento]
  const baseOrtonormal: MovimentoVertical[] = []

  for (const apoio of apoios) {
    const firmeza = Math.min(1, Math.max(0, apoio.firmeza))
    if (firmeza === 0) continue

    // y(ponto) = y + roll*x - pitch*z
    const vetor: MovimentoVertical = [1, apoio.x, -apoio.z]
    for (const base of baseOrtonormal) {
      const projecao = vetor[0] * base[0] + vetor[1] * base[1] + vetor[2] * base[2]
      vetor[0] -= projecao * base[0]
      vetor[1] -= projecao * base[1]
      vetor[2] -= projecao * base[2]
    }

    const norma = Math.hypot(vetor[0], vetor[1], vetor[2])
    if (norma < 1e-9) continue
    const base: MovimentoVertical = [vetor[0] / norma, vetor[1] / norma, vetor[2] / norma]
    baseOrtonormal.push(base)

    const componente = resultado[0] * base[0] + resultado[1] * base[1] + resultado[2] * base[2]
    resultado[0] -= componente * base[0] * firmeza
    resultado[1] -= componente * base[1] * firmeza
    resultado[2] -= componente * base[2] * firmeza
  }

  return resultado
}

/**
 * Atualiza um passo de simulação da suspensão veicular.
 */
export function atualizarSuspensao(
  estado: EstadoSuspensao,
  dt: number,
  suspensaoAnulada: boolean,
  rolamentoTravado: boolean,
  perturbacao: ForcaPerturbacao = {},
  params: ParametrosSuspensao = PARAMETROS_SUSPENSAO_PADRAO,
  rigidezProgressiva?: RigidezDinamica,
): EstadoSuspensao {
  // Limitar dt para evitar instabilidade numérica
  const delta = Math.min(Math.max(dt, 0.001), 0.05)

  const fatorVert = Math.min(1, Math.max(0, rigidezProgressiva?.vertical ?? (suspensaoAnulada ? 1 : 0)))
  const fatorRoll = Math.min(1, Math.max(0, rigidezProgressiva?.roll ?? (suspensaoAnulada ? 1 : 0)))
  const fatorPitch = Math.min(1, Math.max(0, rigidezProgressiva?.pitch ?? (suspensaoAnulada ? 1 : 0)))
  const fatorZ = Math.min(1, Math.max(0, rigidezProgressiva?.longitudinal ?? (rolamentoTravado ? 1 : 0)))

  if ((suspensaoAnulada && rolamentoTravado) || (fatorVert >= 1 && fatorRoll >= 1 && fatorPitch >= 1 && fatorZ >= 1)) {
    return {
      deslocamentoY: 0,
      velocidadeY: 0,
      anguloRoll: 0,
      velocidadeRoll: 0,
      anguloPitch: 0,
      velocidadePitch: 0,
      deslocamentoZ: 0,
      velocidadeZ: 0,
      suspensaoAnulada: true,
      rolamentoTravado: true,
    }
  }

  let y = estado.deslocamentoY
  let vy = estado.velocidadeY
  let roll = estado.anguloRoll
  let vRoll = estado.velocidadeRoll
  let pitch = estado.anguloPitch
  let vPitch = estado.velocidadePitch
  let z = estado.deslocamentoZ
  let vz = estado.velocidadeZ

  if (suspensaoAnulada || fatorVert >= 1) {
    y = 0
    vy = 0
  } else {
    // Integração Vertical Y com rigidez progressiva
    const kY = params.rigidezMolaY * (1 + 9 * fatorVert)
    const cY = params.amortecimentoY * (1 + 4 * fatorVert)
    const fExtY = (perturbacao.forcaY ?? 0) * (1 - fatorVert)
    const aY = -kY * y - cY * vy + fExtY
    vy += aY * delta
    y += vy * delta
    y *= (1 - 0.75 * fatorVert)
  }

  if (suspensaoAnulada || fatorRoll >= 1) {
    roll = 0
    vRoll = 0
  } else {
    // Integração Roll com rigidez progressiva
    const kRoll = params.rigidezRoll * (1 + 9 * fatorRoll)
    const cRoll = params.amortecimentoRoll * (1 + 4 * fatorRoll)
    const tRoll = (perturbacao.torqueRoll ?? 0) * (1 - fatorRoll)
    const aRoll = -kRoll * roll - cRoll * vRoll + tRoll
    vRoll += aRoll * delta
    roll += vRoll * delta
    roll *= (1 - 0.75 * fatorRoll)
  }

  if (suspensaoAnulada || fatorPitch >= 1) {
    pitch = 0
    vPitch = 0
  } else {
    // Integração Pitch com rigidez progressiva
    const kPitch = params.rigidezPitch * (1 + 9 * fatorPitch)
    const cPitch = params.amortecimentoPitch * (1 + 4 * fatorPitch)
    const tPitch = (perturbacao.torquePitch ?? 0) * (1 - fatorPitch)
    const aPitch = -kPitch * pitch - cPitch * vPitch + tPitch
    vPitch += aPitch * delta
    pitch += vPitch * delta
    pitch *= (1 - 0.75 * fatorPitch)
  }

  if (rolamentoTravado || fatorZ >= 1) {
    z = 0
    vz = 0
  } else {
    // Integração Rolamento Z com travamento progressivo
    const kZ = params.rigidezZ * (1 + 9 * fatorZ)
    const cZ = params.amortecimentoZ * (1 + 4 * fatorZ)
    const fExtZ = (perturbacao.forcaZ ?? 0) * (1 - fatorZ)
    const aZ = -kZ * z - cZ * vz + fExtZ
    vz += aZ * delta
    z += vz * delta
    z *= (1 - 0.75 * fatorZ)
  }

  const apoiosLocais = rigidezProgressiva?.apoiosLocais ?? []
  if (!suspensaoAnulada && apoiosLocais.length > 0) {
    ;[y, roll, pitch] = aplicarVinculosLocais([y, roll, pitch], apoiosLocais)
    ;[vy, vRoll, vPitch] = aplicarVinculosLocais([vy, vRoll, vPitch], apoiosLocais)
  }

  return {
    deslocamentoY: y,
    velocidadeY: vy,
    anguloRoll: roll,
    velocidadeRoll: vRoll,
    anguloPitch: pitch,
    velocidadePitch: vPitch,
    deslocamentoZ: z,
    velocidadeZ: vz,
    suspensaoAnulada: suspensaoAnulada || (fatorVert >= 1 && fatorRoll >= 1 && fatorPitch >= 1),
    rolamentoTravado: rolamentoTravado || fatorZ >= 1,
  }
}

/**
 * Resposta postural da vítima à oscilação da suspensão.
 * O tronco e o pescoço reagem inercialmente à inclinação e aceleração do chassi.
 */
export function calcularPoseComSuspensao(
  poseBase: PoseVitima,
  suspensao: EstadoSuspensao,
  colarCervicalColocado: boolean,
): PoseVitima {
  if (suspensao.suspensaoAnulada) {
    return poseBase
  }

  // A inércia move o corpo em contra-fase com a aceleração / inclinação
  const deflexaoTroncoRoll = -suspensao.anguloRoll * 0.75
  const deflexaoTroncoPitch = -suspensao.anguloPitch * 0.65

  // Se o colar estiver colocado, a cabeça acompanha rigidamente o tronco sem torção própria
  const deflexaoCabecaRoll = colarCervicalColocado ? 0 : -suspensao.anguloRoll * 0.85
  const deflexaoCabecaPitch = colarCervicalColocado ? 0 : -suspensao.anguloPitch * 0.75

  return {
    ...poseBase,
    troncoLateralRad: poseBase.troncoLateralRad + deflexaoTroncoRoll,
    troncoInclinacaoRad: poseBase.troncoInclinacaoRad + deflexaoTroncoPitch,
    cabecaTorcaoRad: colarCervicalColocado ? 0 : poseBase.cabecaTorcaoRad,
    cabecaInclinacaoRad: colarCervicalColocado
      ? 0
      : poseBase.cabecaInclinacaoRad + deflexaoCabecaPitch + deflexaoCabecaRoll * 0.5,
  }
}

/**
 * Calcula a sobrecarga cervical instantânea induzida pela oscilação da suspensão.
 * Retorna valor de 0 a 100% e leitura clínica.
 */
export function calcularSobrecargaCervicalSuspensao(
  suspensao: EstadoSuspensao,
  colarCervicalColocado: boolean,
): { sobrecargaPct: number; nivel: 'estavel' | 'atencao' | 'critico'; leitura: string } {
  if (suspensao.suspensaoAnulada) {
    return {
      sobrecargaPct: 0,
      nivel: 'estavel',
      leitura: 'Suspensão anulada: chassi imóvel sobre calços, coluna cervical preservada.',
    }
  }

  const magnitudeMovimento =
    Math.abs(suspensao.anguloRoll) * 2.2 +
    Math.abs(suspensao.anguloPitch) * 2.0 +
    Math.abs(suspensao.deslocamentoY) * 3.5 +
    Math.abs(suspensao.deslocamentoZ) * 4.0

  const fatorColar = fatorProtecaoColar(colarCervicalColocado)
  const bruta = Math.min(100, magnitudeMovimento * 85)
  const final = Math.round(bruta * fatorColar)

  if (final < 15) {
    return {
      sobrecargaPct: final,
      nivel: 'estavel',
      leitura: colarCervicalColocado
        ? 'Movimento residual contido pelo colar cervical.'
        : 'Veículo em repouso relativo.',
    }
  }

  if (final < 50) {
    return {
      sobrecargaPct: final,
      nivel: 'atencao',
      leitura: colarCervicalColocado
        ? 'Balanço da suspensão transmitido em bloco; instale calços nas colunas.'
        : 'Atenção: balanço da suspensão mobilizando o pescoço da vítima.',
    }
  }

  return {
    sobrecargaPct: final,
    nivel: 'critico',
    leitura:
      'CRÍTICO: Oscilação descontrolada! Risco severo de agravamento de Trauma Raquimedular (TRM).',
  }
}
